import { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppState } from '../hooks/useAppState';
import { getScenesByIdentity } from '../data/scenes';
import { StepSlider } from '../components/StepSlider';
import type { Category } from '../types';

const categoryLabelMap: Record<Category, string> = {
  automation: '自动化',
  management: '管理',
  content: '内容',
  acquisition: '获客',
};

const easeOut = [0.22, 1, 0.36, 1] as const;

export function SceneFlowPage(): JSX.Element {
  const { state, setSceneIndex } = useAppState();
  const scenes = state.selectedIdentity
    ? getScenesByIdentity(state.selectedIdentity)
    : [];
  const currentIndex = state.currentSceneIndex;
  const currentScene = scenes[currentIndex];

  const [navDirection, setNavDirection] = useState<number>(0);
  const touchStartY = useRef<number>(0);

  const goNext = useCallback(() => {
    if (currentIndex < scenes.length - 1) {
      setNavDirection(1);
      setSceneIndex(currentIndex + 1);
    }
  }, [currentIndex, scenes.length, setSceneIndex]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setNavDirection(-1);
      setSceneIndex(currentIndex - 1);
    }
  }, [currentIndex, setSceneIndex]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const deltaY = touchStartY.current - e.changedTouches[0].clientY;
    if (deltaY > 80) {
      goNext();
    } else if (deltaY < -80) {
      goPrev();
    }
  }, [goNext, goPrev]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.deltaY > 50) {
      goNext();
    } else if (e.deltaY < -50) {
      goPrev();
    }
  }, [goNext, goPrev]);

  if (!state.selectedIdentity) {
    return (
      <div
        style={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#f5f5f7',
          color: '#6e6e73',
          padding: '24px',
          boxSizing: 'border-box',
        }}
      >
        <p style={{ fontSize: '18px', textAlign: 'center', margin: 0 }}>
          请先选择身份
        </p>
        <p style={{ fontSize: '14px', textAlign: 'center', marginTop: '8px', color: '#a1a1a6' }}>
          在首页选择你的职业身份，即可查看专属场景
        </p>
      </div>
    );
  }

  if (scenes.length === 0) {
    return (
      <div
        style={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#f5f5f7',
          color: '#6e6e73',
        }}
      >
        暂无场景数据
      </div>
    );
  }

  const slideVariants = {
    enter: (direction: number) => ({
      y: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      y: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      y: direction > 0 ? '-100%' : '100%',
      opacity: 0,
    }),
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100vh',
        backgroundColor: '#f5f5f7',
        overflow: 'hidden',
        position: 'relative',
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
    >
      <AnimatePresence initial={false} custom={navDirection} mode="popLayout">
        <motion.div
          key={currentScene.id}
          custom={navDirection}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            type: 'tween',
            duration: 0.35,
            ease: easeOut,
          }}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            padding: '16px',
            paddingBottom: 'calc(16px + 52px + env(safe-area-inset-bottom))',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {/* Scene Info Card */}
          <div
            style={{
              flexShrink: 0,
              backgroundColor: '#ffffff',
              borderRadius: '20px',
              padding: '24px',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.03)',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {/* Top: Category + Progress */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  fontSize: '11px',
                  color: '#a1a1a6',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                }}
              >
                {categoryLabelMap[currentScene.category]}
              </span>
              <span
                style={{
                  fontSize: '12px',
                  color: '#c7c7cc',
                  fontWeight: 400,
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '0.02em',
                }}
              >
                {String(currentIndex + 1).padStart(2, '0')} / {String(scenes.length).padStart(2, '0')}
              </span>
            </div>

            {/* Large Index */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  fontSize: '56px',
                  fontWeight: 200,
                  color: 'rgba(0, 0, 0, 0.06)',
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '-0.02em',
                  userSelect: 'none',
                }}
              >
                {String(currentIndex + 1).padStart(2, '0')}
              </div>
            </div>

            {/* Title + Description */}
            <div style={{ textAlign: 'center' }}>
              <h2
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#1a1a1c',
                  margin: '0 0 6px 0',
                  lineHeight: 1.3,
                  letterSpacing: '0.01em',
                }}
              >
                {currentScene.title}
              </h2>
              <p
                style={{
                  fontSize: '14px',
                  color: '#6e6e73',
                  margin: 0,
                  lineHeight: 1.5,
                  fontWeight: 400,
                }}
              >
                {currentScene.description}
              </p>
            </div>
          </div>

          {/* Steps Area */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              backgroundColor: '#ffffff',
              borderRadius: '20px',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.03)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <StepSlider steps={currentScene.steps} />
          </div>

          {/* Progress Dots */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '5px',
              flexShrink: 0,
              paddingBottom: '4px',
            }}
          >
            {Array.from({ length: scenes.length }).map((_, i) => (
              <div
                key={i}
                style={{
                  height: '3px',
                  borderRadius: '1.5px',
                  backgroundColor: i === currentIndex ? '#1a1a1c' : 'rgba(0, 0, 0, 0.08)',
                  width: i === currentIndex ? '18px' : '3px',
                  transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              />
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
